"use client"

import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Button } from "./button"
import { Input } from "../components/Input"
import { Textarea } from "../components/TextArea"
import { sendEnquiryApi } from "../api/sendEnequireyApi"
import { toast } from 'react-toastify';
import FlagsImage from "../assets/image/Flags.png"; // Import the flags image

export function ContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    enquiry: ''
  });

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      // Add styles to prevent scrolling on the body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone_number') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 10) {
        return;
      }
      setFormData({ ...formData, [name]: digitsOnly });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData, phone_number: `+91${formData.phone_number}` };
      await sendEnquiryApi(dataToSend);
      toast.success("Your message has been sent successfully!");
      onClose();
    } catch (error) {
      toast.error("There was an error sending your message.");
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 z-50 animate-in fade-in duration-200 bg-black/20 backdrop-blur-md overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="mx-4 md:mx-8 sm:mx-10 bg-brand-navy rounded-2xl p-3 sm:p-4 md:p-8 w-full max-w-[95%] sm:max-w-[90%] md:max-w-[1000px] relative animate-in slide-in-from-bottom-4 my-4">
        <button onClick={onClose} className="absolute right-3 sm:right-4 top-1 sm:top-2 text-white hover:opacity-75 transition-opacity">
          <X className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>

        <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-8">
          {/* Left side - Contact Info */}
          <div className="text-white space-y-3 sm:space-y-4 md:space-y-8 mt-12">
            <h2 className="text-xl sm:text-2xl md:text-[37.72px] font-[500] leading-tight md:leading-[47.53px] font-outfit">Let&apos;s talk on something <span className="text-brand-gold">great</span> together</h2>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <a href="mailto:support@egtogo.com" className="hover:underline text-xs sm:text-sm md:text-base">
                  support@egtogo.com
                </a>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5342 11.19 18.85C8.77383 17.3147 6.72534 15.2662 5.19 12.85C3.49998 10.2412 2.44824 7.271 2.12 4.18001C2.09501 3.90347 2.12788 3.62477 2.21649 3.36163C2.30511 3.09849 2.44756 2.85669 2.63476 2.65163C2.82196 2.44656 3.0498 2.28271 3.30379 2.17053C3.55777 2.05834 3.83233 2.00027 4.11 2.00001H7.11C7.59531 1.99523 8.06579 2.16708 8.43376 2.48354C8.80173 2.79999 9.04208 3.23945 9.11 3.72001C9.23662 4.68007 9.47145 5.62273 9.81 6.53001C9.94455 6.88793 9.97366 7.27692 9.89391 7.65089C9.81415 8.02485 9.62886 8.36812 9.36 8.64001L8.09 9.91001C9.51356 12.4136 11.5865 14.4865 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9752 14.1859 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <a href="tel:+919955210717" className="hover:underline text-xs sm:text-sm md:text-base">
                  +91 9955210717
                </a>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-75 transition-opacity">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-75 transition-opacity">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-75 transition-opacity">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-75 transition-opacity">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="bg-white rounded-xl p-3 sm:p-4 md:p-6">
            <h3 className="text-[11px] sm:text-xs md:text-[12.57px] font-[600] leading-tight md:leading-[15.09px] font-outfit mb-3 sm:mb-4 text-gray-900">How Can We Help?</h3>

            <form className="space-y-3 sm:space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="first_name" className="text-xs sm:text-sm text-black">
                  Your First Name
                </label>
                <Input id="first_name" name="first_name" placeholder="John" className="border-gray-300 text-sm text-black" onChange={handleChange} />
              </div>

              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="last_name" className="text-xs sm:text-sm text-black">
                  Your Last Name
                </label>
                <Input id="last_name" name="last_name" placeholder="Smith" className="border-gray-300 text-sm text-black" onChange={handleChange} />
              </div>

              <div className="flex items-center space-x-2">
                <div className="space-y-1 sm:space-y-2">
                  <label htmlFor="phone_number" className="text-xs sm:text-sm text-black">
                    Your Phone Number
                  </label>
                  <div className="flex items-center space-x-2">
                    <img
                      src={FlagsImage}
                      alt="India flag"
                      className="h-8 w-8 rounded-l-md"
                  />
                  <h2 className="text-black text-sm border-gray-300 border-r-2 pr-2">+91</h2>
                  <Input maxLength="10" id="phone_number" name="phone_number" type="tel" placeholder="Your Phone Number" className="border-gray-300 text-sm text-black w-[22rem]" onChange={handleChange} />
                  </div>
                </div>
              </div>

              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="email" className="text-xs sm:text-sm text-black">
                  Your Email-ID
                </label>
                <Input id="email" name="email" type="email" placeholder="Your Email" className="border-gray-300 text-sm text-black" onChange={handleChange} required />
              </div>

              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="enquiry" className="text-xs sm:text-sm text-black">
                  Your Enquiry
                </label>
                <Textarea id="enquiry" name="enquiry" rows={3} className="border-gray-300 resize-none text-sm text-black" onChange={handleChange} />
              </div>

              <Button type="submit" className="w-full bg-brand-gold hover:bg-brand-gold-dark text-brand-navy font-semibold text-sm">Submit Message</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
